import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AccessibilityInfo, Alert, Modal, Platform, Pressable, ScrollView, View, findNodeHandle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { SubHeading, Body, Caption } from '@/components/ui/Text';
import type { ItineraryItem } from '@/services/itinerary';
import type { Booking } from '@/services/bookings';
import { ItineraryItemDetailsContent } from './ItineraryItemDetailsContent';
import { canUseGorhomBottomSheet } from '@/utils/runtimeGuards';

type BottomSheetModule = {
  default: any;
  BottomSheetView: any;
};

let cachedBottomSheet: BottomSheetModule | null | undefined;
const loadBottomSheet = (): BottomSheetModule | null => {
  if (cachedBottomSheet !== undefined) return cachedBottomSheet;
  try {
    if (!canUseGorhomBottomSheet()) {
      cachedBottomSheet = null;
      return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('@gorhom/bottom-sheet') as BottomSheetModule;
    cachedBottomSheet = mod;
    return mod;
  } catch {
    cachedBottomSheet = null;
    return null;
  }
};

export type ItineraryItemDetailsSheetProps = {
  visible: boolean;
  item: ItineraryItem | null;
  booking?: Booking | undefined;
  allItems?: ItineraryItem[];
  preferredTravelMode?: 'walking' | 'driving' | 'transit';
  onClose: () => void;
  onEditItem?: (item: ItineraryItem) => void;
  onDeleteItem?: (item: ItineraryItem) => void;
  onViewDocument?: (
    url: string,
    meta?: { offlinePath?: string | null; fileName?: string; contentType?: string | null },
  ) => void;
  canEdit?: boolean;
  tripStartDate?: Date;
  tripDestination?: string;
  currentCity?: string;
  dayTimezone?: string;
  onResolveConflict?: (conflictItem: ItineraryItem) => void;
  tripId?: string;
  trip?: import('@/services/trips').Trip;
  isTripActive?: boolean;
  onOpenMobility?: (itemId: string) => void;
  snapPoints?: (string | number)[];
};

export function ItineraryItemDetailsSheet({
  visible,
  item,
  booking,
  allItems,
  preferredTravelMode,
  onClose,
  onEditItem,
  onDeleteItem,
  onViewDocument,
  canEdit,
  tripStartDate,
  tripDestination,
  currentCity,
  dayTimezone,
  onResolveConflict,
  tripId,
  trip,
  isTripActive = false,
  onOpenMobility,
  snapPoints,
}: ItineraryItemDetailsSheetProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const bottomSheetModule = useMemo(() => loadBottomSheet(), []);
  const BottomSheetComponent = bottomSheetModule?.default;
  const BottomSheetViewComponent = bottomSheetModule?.BottomSheetView;
  const bottomSheetRef = useRef<any>(null);
  const headingRef = useRef<View>(null);
  const [renderHeavyContent, setRenderHeavyContent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!visible) {
      setRenderHeavyContent(false);
      return;
    }
    // Defer heavy content render by a frame so opening the sheet feels instant.
    const id = setTimeout(() => {
      if (!cancelled) setRenderHeavyContent(true);
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const timeout = setTimeout(() => {
      const node = findNodeHandle(headingRef.current);
      if (node) {
        AccessibilityInfo.setAccessibilityFocus(node);
      }
    }, 60);
    return () => clearTimeout(timeout);
  }, [visible]);

  const resolvedSnapPoints = useMemo(() => {
    if (snapPoints?.length) return snapPoints;
    if (Platform.OS === 'web') return ['50%', '92%'];
    return ['65%', '92%'];
  }, [snapPoints]);

  const handleEdit = useCallback(() => {
    if (!item || !onEditItem) return;
    // Ensure the edit UI appears above the details sheet by closing first.
    onClose();
    setTimeout(() => onEditItem(item), 0);
  }, [item, onEditItem, onClose]);

  const handleDelete = useCallback(() => {
    if (!item || !onDeleteItem) return;
    onClose();
    setTimeout(() => onDeleteItem(item), 0);
  }, [item, onDeleteItem, onClose]);

  const handleViewDocument = useCallback(
    (
      url: string,
      meta?: { offlinePath?: string | null; fileName?: string; contentType?: string | null },
    ) => {
      if (!onViewDocument) return;
      const safe = (url || '').trim();
      if (!safe) {
        Alert.alert('Attachment unavailable', 'This attachment has no file link.');
        return;
      }
      onClose();
      setTimeout(() => {
        onViewDocument(safe, meta);
      }, 100);
    },
    [onClose, onViewDocument],
  );

  const content = (
    <View style={{ paddingBottom: insets.bottom + theme.spacing(2) }}>
      <View
        style={{
          paddingHorizontal: theme.spacing(2),
          paddingTop: theme.spacing(1.5),
          paddingBottom: theme.spacing(1),
          borderBottomWidth: 1,
          borderBottomColor: theme.colors.borderMuted + '40',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: theme.spacing(1),
        }}
      >
        <View ref={headingRef} style={{ flex: 1 }} accessible accessibilityRole="header">
          <SubHeading numberOfLines={1} style={{ fontSize: 16, fontWeight: '800' }}>
            {item?.title || 'Details'}
          </SubHeading>
          <Body color={theme.colors.textSecondary} style={{ fontSize: 12, marginTop: 2 }}>
            Tap outside to close
          </Body>
        </View>
        <Pressable
          onPress={onClose}
          style={({ pressed }) => ({
            padding: theme.spacing(0.75),
            borderRadius: theme.radii.pill,
            backgroundColor: theme.colors.borderMuted + '35',
            opacity: pressed ? 0.7 : 1,
          })}
          accessibilityRole="button"
          accessibilityLabel="Close details"
        >
          <Ionicons name="close" size={18} color={theme.colors.text} />
        </Pressable>
      </View>

      {!item ? (
        <View style={{ padding: theme.spacing(2) }}>
          <Caption style={{ color: theme.colors.textSecondary }}>No item selected</Caption>
        </View>
      ) : (
        <View style={{ padding: theme.spacing(2) }}>
          <ItineraryItemDetailsContent
            item={item}
            booking={booking}
            tripId={tripId}
            trip={trip}
            isTripActive={isTripActive}
            onOpenMobility={onOpenMobility}
            onEditItem={onEditItem ? () => handleEdit() : undefined}
            onDeleteItem={onDeleteItem ? () => handleDelete() : undefined}
            onViewDocument={handleViewDocument}
            canEdit={Boolean(canEdit)}
            onMarkDoneSuccess={onClose}
          />
        </View>
      )}
    </View>
  );

  // Fallback: render a Modal overlay when bottom-sheet isn't available.
  if (!BottomSheetComponent || !BottomSheetViewComponent) {
    return (
      <Modal
        visible={visible}
        animationType="slide"
        transparent
        onRequestClose={onClose}
        presentationStyle="overFullScreen"
        accessibilityViewIsModal
      >
        <View style={{ flex: 1, backgroundColor: theme.colors.overlay, justifyContent: 'flex-end' }}>
          <Pressable style={{ flex: 1 }} onPress={onClose} />
          <View
            style={{
              backgroundColor: theme.colors.surface,
              borderTopLeftRadius: theme.radii.xl,
              borderTopRightRadius: theme.radii.xl,
              maxHeight: '92%',
              overflow: 'hidden',
            }}
          >
            <ScrollView contentContainerStyle={{ paddingBottom: theme.spacing(2) + insets.bottom }}>
              {content}
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  }

  // When using BottomSheet, we mount it always (so it can animate) and control via index.
  // If item is null, we still allow it to render but show empty state.
  return (
    <BottomSheetComponent
      ref={bottomSheetRef}
      index={visible ? 0 : -1}
      snapPoints={resolvedSnapPoints}
      enablePanDownToClose
      onClose={onClose}
      backgroundStyle={{
        backgroundColor: theme.colors.surface,
        borderTopLeftRadius: theme.radii.xl,
        borderTopRightRadius: theme.radii.xl,
      }}
      handleIndicatorStyle={{ backgroundColor: theme.colors.borderMuted }}
    >
      <BottomSheetViewComponent accessibilityViewIsModal>{content}</BottomSheetViewComponent>
    </BottomSheetComponent>
  );
}


